import React, {useEffect, useMemo, useRef} from 'react';
import {useTheme, Flex, Spacer, Box} from '@chakra-ui/react';
import ConvinceMeToGoButton from '../../../pages/Event/ConvinceMeToGoButton';
import {TextG10, TextG14} from '../../typography';
import Card from '../../Card';
import {Event} from '../../../types/article';
import {generatePath, useLocation, useNavigate} from 'react-router-dom';
import {ROUTES} from '../../../constants/routes';
import DefaultEventImage from '../../../assets/images/default_event.png';
import {getEventDate} from '../../../utils/date';
import CardInterestTags from '../CardInterestTags';
import useGetUserLocation from '../../../hooks/useUserLocation';
import {useTrackVisibility} from 'react-intersection-observer-hook';
import useVisibleArticles from '../../../hooks/useVisible';
import {getDisplayPrice} from '../../../utils/price';
import DotSeparator from '../../DotSeparator';
import Icon from '../../Icon';
import {useOffers} from '../../../hooks/useOffers';
import OffersCardBanner from '../../OffersCardBanner';
import {useFetchEventRSVPStats, useFetchEventInterestedGoingUsers} from '../../../hooks/useEvent';
import {calculateDistance, getDistanceString} from '../../../utils/distance';
import useAuth from '../../../hooks/useAuth';
import PremiumEventCard from './PremiumEventCard';
import NonPremiumEventCard from './NonPremiumEventCard';
import {storeScrollPositionInSession} from '../../../utils/scroll';
import RSVPController from '../../../pages/Event/RSVP/RSVPController';
import {BlueButton} from '../../button';
import {useFetchUserTickets} from '../../../hooks/useUserTickets';
import {filter} from 'lodash';
import MeetUpCard from '../MeetUpCard';

const iconStyle = {
  color: 'grey',
  fontSize: '12px',
  marginRight: '5px',
};

const optionStyle: React.CSSProperties = {
  fontSize: '18px',
  paddingTop: '3px',
};

const EventCard = ({
  event,
  leftTopItem,
  rightTopItem,
  fullCard = true,
  height,
  modal,
  hideRSVP,
  isFeatured = false,
  isSlide,
  onClick,
  isPreview = false,
  forcePremium = false,
  hideInterestToggle = false,
}: {
  event?: Event;
  leftTopItem?: any;
  rightTopItem?: any;
  fullCard?: boolean;
  height?: string;
  modal?: boolean;
  hideRSVP?: boolean;
  isFeatured?: boolean;
  isSlide?: boolean;
  onClick?: (e) => void;
  isPreview?: boolean;
  forcePremium?: boolean;
  hideInterestToggle?: boolean;
}) => {
  if (!event) return null; // TODO: revisit the loading flow to prevent this check
  const theme = useTheme();
  let userLocation;
  if (!isPreview) {
    userLocation = useGetUserLocation();
  }
  const cardChildContent = useRef<HTMLDivElement>(null);
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const [ref, {wasEverVisible, isVisible}] = useTrackVisibility();
  const {addVisibleArticle} = useVisibleArticles();
  const {user} = useAuth();

  const fetchTickets = useFetchUserTickets(!!user);
  const tickets = fetchTickets.data?.data;
  const eventTickets = filter(tickets, (ticket) => ticket.event_id == event.id);
  const userHasTickets = eventTickets && eventTickets.length > 0;

  const isWalkaboutEvent = Boolean(event.is_walkabout_official);

  const fetchOffers = useOffers({
    articleType: 'eventId',
    articleId: event.id.toString(),
    enabled: !onClick && isVisible && !!user,
  });

  const {data: interestedData} = useFetchEventInterestedGoingUsers(event.id);
  const usersInterested = interestedData?.pages[0]?.data?.total || 0;

  const activeOffers = useMemo(() => {
    if (fetchOffers && fetchOffers.offers && fetchOffers.offers.length) {
      return <OffersCardBanner fullCard={fullCard} offers={fetchOffers.offers} />;
    }
  }, [fetchOffers, theme]);

  useEffect(() => {
    if (wasEverVisible && event?.id.toString()) {
      addVisibleArticle({id: event.id.toString(), type: 'event'});
    }
  }, [wasEverVisible]);

  const eventDate = useMemo(
    () =>
      !!event.is_recurring && event.recurring_schedule
        ? event.recurring_schedule
        : getEventDate(event.start_date_time, event.end_date_time),
    [event]
  );

  if (event.is_meetup && !forcePremium) {
    return <MeetUpCard meet-up={event} />;
  }

  if (!isFeatured) {
    if (event.is_premium || forcePremium)
      return (
        <PremiumEventCard
          height={height}
          onClick={onClick}
          event={event}
          leftTopItem={leftTopItem}
          rightTopItem={rightTopItem}
          isSlide={isSlide}
          modal={modal}
          isPreview={isPreview}
          hideInterestToggle={hideInterestToggle}
        />
      );
    else
      return (
        <NonPremiumEventCard
          height={height}
          onClick={onClick}
          event={event}
          leftTopItem={leftTopItem}
          rightTopItem={rightTopItem}
          isSlide={isSlide}
          modal={modal}
          isPreview={isPreview}
          hideInterestToggle={hideInterestToggle}
        />
      );
  }

  return (
    <>
      <Card
        article={event}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
            return;
          }
          if (
            e?.target.tagName === 'BUTTON' ||
            e?.target.parentNode.tagName === 'BUTTON' ||
            e?.target.parentNode.parentNode.tagName === 'BUTTON'
          )
            return;

          const route = modal
            ? {
                pathname,
                search: `?id=${event.id.toString()}&articleType=${
                  event.type ? event.type.toLowerCase() : 'event'
                }`,
              }
            : generatePath(ROUTES.EVENT, {
                eventId: event.id.toString(),
              });
          storeScrollPositionInSession();
          navigate(route);
        }}
        overlayBackgroundColor={isFeatured ? '#2729295C' : ''}
        overlayHeight={
          isFeatured && cardChildContent?.current?.clientHeight
            ? `${cardChildContent.current.clientHeight + 24}px`
            : ''
        }
        borderRadius={isFeatured ? '0px' : '12px'}
        childPaddingX={!fullCard ? 3 : 4}
        height={height}
        coverImage={event.image_url || DefaultEventImage}
        leftTopItem={!fullCard && activeOffers ? activeOffers : leftTopItem}
        rightTopItem={rightTopItem}>
        <Box ref={cardChildContent}>
          <Flex ref={ref} py={1} alignItems="center" textAlign="center">
            {/* {!!event.is_recurring && <Icon iconName="fi-rr-time-past" style={iconStyle} />} */}
            <TextG10 color={theme.colors.white}>
              {!!event.is_recurring ? `${eventDate} (Recurring)` : eventDate}
            </TextG10>
            {event.price && fullCard && (
              <>
                <DotSeparator />
                <TextG10 color={theme.colors.white}>
                  {getDisplayPrice(event.price, event?.max_price, event?.price_override)}
                </TextG10>
              </>
            )}
            {fullCard && activeOffers}
          </Flex>
          <Flex py={1} alignItems={'bottom'} width={'100%'} justifyContent={'space-between'}>
            <TextG14
              isTruncated="&hellip;"
              noOfLines={3}
              fontWeight={500}
              color={theme.colors.white}>
              {event.title}
            </TextG14>
          </Flex>
          <Flex my={1} justifyContent="space-between" align="center">
            {!isFeatured && event.topics_of_interest && event.topics_of_interest.length > 0 && (
              <CardInterestTags
                autoScroll={true}
                tagsVisible={true}
                onlyShowMatched={!fullCard}
                topicsOfInterest={event.topics_of_interest}
              />
            )}
            <Spacer />
          </Flex>
          <Flex alignItems="center" justifyContent="space-between" gap="0.3rem">
            {!onClick && event.has_tickets && (
              <BlueButton
                w="120px"
                height="30px"
                onClick={() =>
                  userHasTickets
                    ? navigate(`/tickets/${event.id}`)
                    : navigate(`/event/${event.id}/tickets`, {replace: true})
                }>
                <TextG10 fontWeight="500">{userHasTickets ? 'See Tickets' : 'Get Tickets'}</TextG10>
              </BlueButton>
            )}
            {isWalkaboutEvent && !onClick && (
              <ConvinceMeToGoButton
                event={event}
                typographySize="sm"
                textColor={event.has_tickets || event.rsvp_status?.status ? 'black' : 'white'}
                background={event.has_tickets ? 'white' : '#276ACD'}
                buttonProps={{
                  flexGrow: 1,
                  height: '2rem',
                  border: '1px solid white',
                  maxW: fullCard ? '50%' : '',
                }}
              />
            )}
            {isWalkaboutEvent && !onClick && !hideRSVP && !event.has_tickets && (
              <RSVPController
                color={isFeatured ? 'white' : 'black'}
                event={event}
                buttonProps={{
                  flexGrow: 0.5,
                  height: '2rem',
                  border: '1px solid white',
                  maxW: fullCard ? '50%' : '',
                }}
                variant="sm"
              />
            )}

            <Flex flexDir="column" gap="8px">
              {fullCard && event.latitude && event.longitude && userLocation?.data && (
                <>
                  <TextG10 sx={{wordWrap: 'normal !important'}} color={theme.colors.white}>
                    {getDistanceString(
                      calculateDistance(
                        {
                          latitude: userLocation?.data?.latitude,
                          longitude: userLocation?.data?.longitude,
                        },
                        {latitude: Number(event.latitude), longitude: Number(event.longitude)}
                      )
                    )}
                  </TextG10>
                </>
              )}
              {fullCard && usersInterested > 0 && (
                <TextG10
                  alignSelf="end"
                  whiteSpace="nowrap"
                  color={theme.colors.white}>{`${usersInterested} Interested`}</TextG10>
              )}
            </Flex>
          </Flex>
        </Box>
      </Card>
    </>
  );
};

export default EventCard;
